/* eslint-disable @typescript-eslint/no-explicit-any */
import { RenderingSDK } from '@rendering/web-client';
import { CANCEL } from 'redux-saga';
import { CancelablePromise } from 'src/types/upload';

const TENANT = 'default';

/** Error code the SDK throws when a request is aborted via its signal */
const ABORTED = 'REQUEST_ABORTED';

const uploadsDomains: Record<string, string> = {
    auto: 'https://uploads.documents.cimpress.io',
};

function getDomain(region = 'auto') {
    return uploadsDomains[region] || uploadsDomains.auto;
}

/**
 * Makes a request cancelable by Redux Saga task cancellation and by hand.
 * Aborted requests never settle, matching the legacy fetchClient behaviour so
 * cancellation leaves no unhandled rejection behind.
 *
 * @param run - Receives the abort signal and performs the request
 * @returns Promise carrying both `cancel()` and the Redux Saga `CANCEL` symbol
 */
function toCancelable<T>(run: (signal: AbortSignal) => Promise<T>): CancelablePromise<T> {
    const abortController = new AbortController();

    const cancelablePromise = run(abortController.signal).catch((error) => {
        if (error?.code === ABORTED) {
            // Never settle, so a cancelled saga leaves no unhandled rejection
            return new Promise<T>(() => {});
        }
        throw error;
    }) as CancelablePromise<T>;

    const cancel = () => abortController.abort();
    cancelablePromise.cancel = cancel;
    cancelablePromise[CANCEL] = cancel;

    return cancelablePromise;
}

export function generateUploadsUrl(region = 'auto', uploadId = '', resizeParams?: Record<string, any>) {
    let url = `${getDomain(region)}/v1/uploads/${uploadId}?tenant=${TENANT}`;
    if (resizeParams) {
        url += `&transform=${encodeURIComponent(
            `[{"transform":"scaleDown","arguments":{x:${resizeParams.width},y:${resizeParams.height}}}]`,
        )}`;
    }
    return url;
}

/**
 * Uploads the scene XML. Returns the upload id.
 * Cancelable: aborts the request when the calling saga task is cancelled.
 */
export function uploadSceneXml(sdk: RenderingSDK, xml: string): CancelablePromise<string> {
    const sceneXml = `<?xml version="1.0" encoding="utf-16"?>${xml}`;
    const formData = new FormData();
    const blob = new Blob([sceneXml], { type: 'text/xml' });
    formData.append('file', blob, 'index.xml');

    return toCancelable((signal) =>
        sdk.Uploads.uploadBinaryFile({
            data: formData,
            tenant: TENANT,
            signal,
        }).then((response) => response.uploadId),
    );
}

// Returns the upload id
export function uploadJSONFile(sdk: RenderingSDK, json: Record<string, any>): Promise<string> {
    const formData = new FormData();
    const blob = new Blob([JSON.stringify(json)], { type: 'application/json' });
    formData.append('file', blob, 'document.json');

    return sdk.Uploads.uploadBinaryFile({
        data: formData,
        tenant: TENANT,
    }).then((response) => response.uploadId);
}

export function uploadImage(sdk: RenderingSDK, file: File): Promise<{ uploadId: string }> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    return sdk.Uploads.uploadBinaryFile({
        data: formData,
        tenant: TENANT,
    });
}

export function getUrlById(uploadId: string) {
    return `${uploadsDomains.auto}/v1/uploads/${uploadId}`;
}
